import { isImageFile } from './fileType';

/**
 * Compute a posix-style relative path from `fromDir` to `toPath`. Both
 * arguments must be absolute (or share a common root). Walks the shared
 * prefix and emits `../` for each remaining segment of `fromDir`, then the
 * remaining `toPath` segments.
 *
 * Examples:
 *   pathRelative('/a/b', '/a/b/c/x.md')      → 'c/x.md'
 *   pathRelative('/a/b/c', '/a/b/x.md')      → '../x.md'
 *   pathRelative('/a/b/c', '/a/d/x.md')      → '../../d/x.md'
 *   pathRelative('/a/b', '/a/b')             → '.'
 */
export function pathRelative(fromDir: string, toPath: string): string {
  const norm = (p: string): string => p.replace(/\\/g, '/');
  const fromSegs = norm(fromDir).split('/').filter(Boolean);
  const toSegs = norm(toPath).split('/').filter(Boolean);
  let i = 0;
  while (i < fromSegs.length && i < toSegs.length && fromSegs[i] === toSegs[i]) i++;
  const up = fromSegs.slice(i).map(() => '..');
  const down = toSegs.slice(i);
  const parts = [...up, ...down];
  return parts.length === 0 ? '.' : parts.join('/');
}

function basename(p: string): string {
  return p.replace(/[\\/]+$/, '').split(/[\\/]/).pop() ?? p;
}

/**
 * Escape characters that would break a markdown link target. `]`, `)`,
 * spaces inside the URL need encoding; for simplicity we only encode the
 * minimal set known to confuse common parsers.
 */
function encodeLinkTarget(href: string): string {
  return href.replace(/ /g, '%20').replace(/\(/g, '%28').replace(/\)/g, '%29');
}

/**
 * Build a markdown snippet for a batch of dragged sidebar files. Images
 * render as `![name](rel)` embeds; other files render as `[name](rel)`
 * links. Paths are emitted relative to `docDir` so the saved markdown
 * stays portable. Each item is followed by a newline so multiple files
 * stack vertically rather than chaining on one line.
 *
 * When `docDir` is empty (untitled doc), absolute paths are used as a
 * fallback so the link/image is at least readable.
 */
export function formatSourcesAsMarkdown(sources: readonly string[], docDir: string): string {
  if (sources.length === 0) return '';
  return sources
    .map((src) => {
      const rel = docDir ? pathRelative(docDir, src) : src;
      const href = encodeLinkTarget(rel);
      const name = basename(src);
      return isImageFile(name) ? `![${name}](${href})` : `[${name}](${href})`;
    })
    .join('\n');
}
